import React from "react";
import toast from "react-hot-toast";
import useApi from "../useApi";

type StatusEntry = {
  status: string;
  count: number;
  percentage: number;
};

type PatientsStatusRes = {
  status: string;
  data: StatusEntry[];
};

type ChartData = {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string[];
    borderWidth: number;
  }[];
};

export type StatusData = {
  active: { count: number; percentage: number };
  inactive: { count: number; percentage: number };
  pending: { count: number; percentage: number };
  total: number;
};

const usePatientsStatusData = (): {
  patientsChartData: ChartData | undefined;
  statusData: StatusData | undefined;
  getPatientsStatusData: () => Promise<void>;
  patientsStatusLoading: boolean;
} => {
  const [patientsChartData, setPatientsChartData] = React.useState<ChartData | undefined>();
  const [statusData, setStatusData] = React.useState<StatusData | undefined>();

  const { loading: patientsStatusLoading, makeApiCall } = useApi({
    url: `/rtm/dashboard/patient-status`,
    method: "GET",
  });

  const getPatientsStatusData = async () => {
    const { response, error } = await makeApiCall<undefined, PatientsStatusRes>();

    if (response?.status === "success") {
      let activeCount = 0;
      let inactiveCount = 0;
      let pendingCount = 0;
      const colors = ["#1D4E87", "#0170AE","#5e5e5e"];
      response.data.forEach(item => {
        if (item.status === "Active") {
          activeCount = item.count;
        } else if (item.status === "Inactive") {
          inactiveCount = item.count;
        } else if (item.status === "Pending") {
          pendingCount = item.count;
        }
      });

      const total = activeCount + inactiveCount + pendingCount;
      
      const transformedChartData: ChartData = {
        labels: ["Active", "Inactive", "Pending"],
        datasets: [
          {
            label: "Patients",
            data: [activeCount, inactiveCount, pendingCount],
            backgroundColor: colors,
            borderWidth: 1,
          },
        ],
      };

      setStatusData({
        active: { count: activeCount, percentage: Math.round((activeCount / total) * 100) },
        inactive: { count: inactiveCount, percentage: Math.round((inactiveCount / total) * 100) },
        pending: { count: pendingCount, percentage: Math.round((pendingCount / total) * 100) },
        total
      });

      setPatientsChartData(transformedChartData);
    } else {
      setPatientsChartData(undefined);
    }

    if (error) {
      toast.error("Failed to fetch patients status data. Please try again later.");
    }
  };

  return {
    patientsChartData,
    statusData,
    getPatientsStatusData,
    patientsStatusLoading,
  } as const;
};

export default usePatientsStatusData;